/**
 * B.7.e / J.3 — Portal-wizard shell.
 *
 * Four-step wizard to clone a portal template into a new
 * `/c/${slug}/desktop` deployment. Each step is its own component;
 * `WizardShell` owns the navigation state + the cross-step accumulators
 * (template selection, uploaded files, sanitize results, job id).
 *
 * **J.3 (2026-05-20):** the operator-role gate was DELETED per the
 * locked user direction "all users should be able to create their own
 * portal". Every signed-in user can drive the wizard now. Server-side,
 * the Convex `portalProvisioning.{listTemplates, getTemplate,
 * createTemplate, cloneTemplate, getJob}` calls use `requireIdentity`
 * (not `requireOperator`) so the wizard works for any authed user.
 *
 * The wizard is still useful alongside the J.3 WorkspaceSetupPanel —
 * the panel is the lightweight "pick a slug, mint a workspace" flow
 * while the wizard handles the heavy "upload + sanitize + provision"
 * path.
 */

import { useState } from 'react';
import { testIds } from '../../lib/testIds';
import type { Id } from '../../../convex/_generated/dataModel';
import { Step1Template } from './Step1Template';
import { Step2Upload, type UploadedFile } from './Step2Upload';
import { Step3Sanitize, type SanitizeOutcome } from './Step3Sanitize';
import { Step4Provision } from './Step4Provision';
import './WizardShell.css';

const WIN_FONT =
  "var(--xp-font-body)";

export interface WizardState {
  step: 1 | 2 | 3 | 4;
  templateId: Id<'portalTemplates'> | null;
  targetClientSlug: string;
  uploadedFiles: UploadedFile[];
  sanitizeOutcomes: Record<string, SanitizeOutcome>;
  jobId: Id<'portalProvisioningJobs'> | null;
}

const STEPS: ReadonlyArray<{ n: 1 | 2 | 3 | 4; label: string }> = [
  { n: 1, label: 'Template' },
  { n: 2, label: 'Upload' },
  { n: 3, label: 'Sanitize' },
  { n: 4, label: 'Provision' },
];

export function WizardShell() {
  const [state, setState] = useState<WizardState>({
    step: 1,
    templateId: null,
    targetClientSlug: '',
    uploadedFiles: [],
    sanitizeOutcomes: {},
    jobId: null,
  });

  return (
    <div
      data-testid={testIds.portalWizard.root}
      className="text-black p-6"
      style={{ fontFamily: WIN_FONT, fontSize: 13, lineHeight: 1.45 }}
    >
      <h1 className="text-2xl font-semibold mb-2">Create a new portal</h1>
      <p className="text-sm text-gray-800 mb-6">
        Clone a template, upload firm-supplied source docs for sanitisation,
        and provision the new SharePoint site.
      </p>

      {/* Step indicator */}
      <ol
        data-testid={testIds.portalWizard.stepIndicator}
        className="flex items-center gap-4 mb-8 border-b border-black/30 pb-3"
      >
        {STEPS.map(({ n, label }) => {
          const active = state.step === n;
          const done = state.step > n;
          return (
            <li
              key={n}
              data-testid={testIds.portalWizard.stepNumber(n)}
              data-active={active ? 'true' : 'false'}
              data-done={done ? 'true' : 'false'}
              className={`flex items-center gap-2 text-sm ${
                active ? 'font-semibold text-black' : 'text-gray-700'
              }`}
            >
              <span
                className={`inline-flex h-6 w-6 items-center justify-center border ${
                  active
                    ? 'border-black bg-black text-white'
                    : done
                      ? 'border-black bg-gray-200 text-black'
                      : 'border-black/40 bg-white text-gray-700'
                }`}
              >
                {n}
              </span>
              {label}
            </li>
          );
        })}
      </ol>

      {state.step === 1 && (
        <Step1Template
          state={state}
          onContinue={(templateId, targetClientSlug) =>
            setState((s) => ({ ...s, templateId, targetClientSlug, step: 2 }))
          }
        />
      )}
      {state.step === 2 && (
        <Step2Upload
          state={state}
          onBack={() => setState((s) => ({ ...s, step: 1 }))}
          onContinue={(uploadedFiles) =>
            setState((s) => ({ ...s, uploadedFiles, step: 3 }))
          }
        />
      )}
      {state.step === 3 && (
        <Step3Sanitize
          state={state}
          onBack={() => setState((s) => ({ ...s, step: 2 }))}
          onContinue={(sanitizeOutcomes) =>
            setState((s) => ({ ...s, sanitizeOutcomes, step: 4 }))
          }
        />
      )}
      {state.step === 4 && (
        <Step4Provision
          state={state}
          onBack={() => setState((s) => ({ ...s, step: 3 }))}
          onJobCreated={(jobId) => setState((s) => ({ ...s, jobId }))}
        />
      )}
    </div>
  );
}
